import Box from '@mui/material/Box';
import ImageListItem from '@mui/material/ImageListItem';
import type { ReactNode } from 'react';

import { EvidenceImage } from '../EvidenceImage/EvidenceImage';
import type { PhotoEvidenceItem } from '../PhotoEvidenceList/PhotoEvidenceList';
import { Typography } from '../Typography/Typography';

type PhotoEvidenceListItemProps = {
  photo: PhotoEvidenceItem;
  placeholderSrc?: string;
  openOnClick?: boolean;
  onClick?: (photo: PhotoEvidenceItem) => void;
  obfuscateAi?: boolean;
  valueCounter?: ReactNode;
  imageLoadingStrategy?: 'progressive' | 'eager';
};

export const PhotoEvidenceListItem = ({
  photo,
  placeholderSrc,
  openOnClick,
  onClick,
  obfuscateAi,
  valueCounter,
  imageLoadingStrategy = 'progressive',
}: PhotoEvidenceListItemProps) => {
  const {
    alt,
    thumbnailSrc,
    placeholderSrc: itemPlaceholderSrc,
    src,
    isLoading: isPhotoLoading,
    uploadProgress,
    cols,
    rows,
    imageRejectionReasons,
    imageWarningReasons,
    analysisStatus,
  } = photo;

  const hasRejectionReasons = imageRejectionReasons && imageRejectionReasons.length > 0;
  const shouldShowRejections = hasRejectionReasons;
  const shouldShowWarnings = false;

  return (
    <ImageListItem
      key={src}
      sx={{
        breakInside: 'avoid',
        cursor: openOnClick ? 'pointer' : 'default',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        pageBreakInside: 'avoid',
      }}
      cols={cols}
      rows={rows}
    >
      {valueCounter}
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <EvidenceImage
          alt={alt}
          src={thumbnailSrc || src}
          onClick={() => openOnClick && onClick?.(photo)}
          loading="lazy"
          placeholderSrc={itemPlaceholderSrc ?? placeholderSrc}
          isLoading={isPhotoLoading}
          uploadProgress={uploadProgress}
          analysisStatus={analysisStatus}
          obfuscateAi={obfuscateAi}
          loadingStrategy={imageLoadingStrategy}
        />
      </Box>
      {shouldShowRejections
        ? imageRejectionReasons?.map((reason) => (
            <Typography key={reason} variant="caption" color="error">
              {reason}
            </Typography>
          ))
        : null}
      {shouldShowWarnings
        ? imageWarningReasons?.map((reason) => (
            <Typography key={reason} variant="caption" color="warning">
              {reason}
            </Typography>
          ))
        : null}
    </ImageListItem>
  );
};
